import React from 'react';
import { useIntl } from 'react-intl';
import { usePageContext } from 'vike-react/usePageContext';
import { useFormatMessage } from '../i18n-review/client/Msg';
import { useSearchFilters, useSearchQuery } from '../contexts/SearchContext';
import { usePushIndexUiState } from './usePushIndexUiState';
import type { IndexUiState } from '../utils/search-router/url-handling';
import { BASE_URL, JOBS_LISTING_SEGMENT } from '../utils/constants/url-constants';
import { FINNISH_LOCALITY_LOCALE_NAMES } from '../utils/geocoding/finnish-locality-locale-names';
import {
  getLocationBackgroundImageSrcset,
  getLocationBackgroundImageDefaultSrc,
} from '../utils/location/get-location-background-image-url';
import {
  resolveBackgroundImageLocation,
  STATIC_LOCATIONS_WITH_BACKGROUNDS,
} from '../utils/location/known-location-backgrounds';
import { generateLocationMarketHeaderText } from '../utils/location/generate-location-market-header-text';
import { formatLocationBackgroundImageAltFromIntl } from '../utils/location/format-location-background-image-alt';
import { buildLocationSalaryPercentileSeoData } from '../utils/location/build-location-salary-percentile-seo';
import { buildEncodedLocationSrpPathSegment } from '../utils/location/build-encoded-location-srp-path-segment';
import { buildActiveFiltersFromInitialFilters } from '../utils/seo/build-active-filters';
import { generateResultsHeading } from '../utils/seo/generate-srp-seo-meta';
import type { LocationTrendsData } from './useLocationTrends';

/** Same locality table as URL routing and job processor (see finnish-locality-locale-names.ts). */
const CITY_NAME_MAPPINGS = FINNISH_LOCALITY_LOCALE_NAMES;

/**
 * Country name mappings
 */
const COUNTRY_NAME_MAPPINGS: Record<string, { fi: string; sv: string; en: string }> = {
  'Finland': { fi: 'Suomi', sv: 'Finland', en: 'Finland' },
  'Suomi': { fi: 'Suomi', sv: 'Finland', en: 'Finland' },
};

export interface UseMarketHeaderDataProps {
  location: string;
  /** Locations that currently have a background image (static list plus Lambda-discovered ones). */
  knownLocationBackgrounds?: string[];
  nbHits: number;
  description?: string;
  backgroundImage?: string; // Optional override, otherwise auto-detected from location
  locationTrendsData?: LocationTrendsData | null;
}

/**
 * All derived data for the interactive MarketHeader — extracted verbatim (same formulas,
 * same memoization) so MarketRail (desktop) and MarketStatCards (mobile) share one source of
 * truth and render the JSON-LD / sr-only SEO content identically. Presentation-only state
 * (collapse toggle, background brightness, viewport tier) stays local to each presentation
 * component since the two layouts manage it differently.
 */
export function useMarketHeaderData({
  location,
  knownLocationBackgrounds,
  nbHits,
  description,
  backgroundImage,
  locationTrendsData,
}: UseMarketHeaderDataProps) {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const { filters } = useSearchFilters();
  const { query } = useSearchQuery();
  const { pushIndexUiState } = usePushIndexUiState();
  const pageContext = usePageContext();

  // Parse filters
  const parsedFilters = React.useMemo(() => {
    if (!filters) return {};
    if (typeof filters === 'string') {
      try {
        return JSON.parse(filters) as Record<string, string[]>;
      } catch {
        return {};
      }
    }
    return filters as Record<string, string[]>;
  }, [filters]);

  // Extract data from locationTrendsData (available from SSR)
  const salaryStats = locationTrendsData?.salaryStats?.aggregate;
  const remotenessDistribution = locationTrendsData?.trends?.remotenessDistribution || {};
  const languageRequirements = locationTrendsData?.trends?.languageRequirements || {};

  // Calculate sample count for samples button (must work server-side)
  // Priority: salaryStats.sampleSize > companyDistribution count > nbHits (as fallback)
  // This ensures the samples button renders server-side when locationTrendsData is available from SSR
  // Uses source data directly to avoid dependency issues during SSR
  const sampleCount = React.useMemo(() => {
    // Check salaryStats.sampleSize first (most accurate)
    const salarySampleSize = locationTrendsData?.salaryStats?.aggregate?.sampleSize;
    if (salarySampleSize && salarySampleSize > 0) return salarySampleSize;

    // Check companyJobCount (total jobs across all companies, pre-computed before capping)
    const companyJobCount = locationTrendsData?.trends?.companyJobCount;
    if (companyJobCount && companyJobCount > 0) return companyJobCount;
    // Fallback: sum capped companyDistribution entries (approximate)
    if (locationTrendsData?.trends?.companyDistribution) {
      const count = Object.values(locationTrendsData.trends.companyDistribution).reduce(
        (sum, count) => sum + (typeof count === 'number' ? count : 0),
        0
      );
      if (count > 0) return count;
    }

    // Use nbHits as final fallback (always available from search results)
    return nbHits || 0;
  }, [
    locationTrendsData?.salaryStats?.aggregate?.sampleSize,
    locationTrendsData?.trends?.companyDistribution,
    nbHits,
  ]);

  // Calculate salary range from stats
  const salaryRange = React.useMemo(() => {
    if (!salaryStats) return null;
    const min = salaryStats.min || 0;
    const max = salaryStats.max || 0;
    const percentile25 = salaryStats.percentile25?.min || 0;
    const percentile75 = salaryStats.percentile75?.min || 0;

    if (min === 0 && max === 0) return null;

    // Show 25th-75th percentile range if available, otherwise min-max
    if (percentile25 > 0 && percentile75 > 0) {
      return {
        low: percentile25,
        high: percentile75,
        isPercentile: true,
      };
    }

    return {
      low: min,
      high: max,
      isPercentile: false,
    };
  }, [salaryStats]);
  const salaryPercentileSeoData = React.useMemo(
    () => buildLocationSalaryPercentileSeoData(salaryStats),
    [salaryStats],
  );

  // Calculate work model percentages and determine headline
  const workModelData = React.useMemo(() => {
    const onsite = remotenessDistribution.onsite || 0;
    const hybrid = remotenessDistribution.hybrid || 0;
    const remote = remotenessDistribution.remote || 0;
    const total = onsite + hybrid + remote;

    if (total === 0) {
      return { onsite, hybrid, remote, total, headline: null, breakdown: null, breakdownData: null };
    }

    const onsitePercent = (onsite / total) * 100;
    const hybridPercent = (hybrid / total) * 100;
    const remotePercent = (remote / total) * 100;
    const flexiblePercent = hybridPercent + remotePercent;

    // Determine headline based on distribution
    let headlineKey: string;
    let headlineDefault: string;
    if (onsitePercent > 60) {
      headlineKey = 'marketHeader.workModel.primarilyOnsite';
      headlineDefault = 'Primarily Onsite';
    } else if (hybridPercent > 40 || (hybridPercent > onsitePercent && hybridPercent > remotePercent)) {
      headlineKey = 'marketHeader.workModel.hybridPopular';
      headlineDefault = 'Hybrid Popular';
    } else if (remotePercent > 30 || (remotePercent > onsitePercent && remotePercent > hybridPercent)) {
      headlineKey = 'marketHeader.workModel.remoteFriendly';
      headlineDefault = 'Remote-Friendly';
    } else if (flexiblePercent > 60) {
      headlineKey = 'marketHeader.workModel.flexibleWork';
      headlineDefault = 'Flexible Work';
    } else if (onsitePercent > hybridPercent && onsitePercent > remotePercent) {
      headlineKey = 'marketHeader.workModel.primarilyOnsite';
      headlineDefault = 'Primarily Onsite';
    } else if (hybridPercent > remotePercent) {
      headlineKey = 'marketHeader.workModel.hybridPopular';
      headlineDefault = 'Hybrid Popular';
    } else {
      headlineKey = 'marketHeader.workModel.remoteFriendly';
      headlineDefault = 'Remote-Friendly';
    }

    // Create breakdown text - will be translated in the component
    const breakdownData = {
      onsitePercent: onsitePercent > 0 ? Math.round(onsitePercent) : null,
      hybridPercent: hybridPercent > 0 ? Math.round(hybridPercent) : null,
      remotePercent: remotePercent > 0 ? Math.round(remotePercent) : null,
    };
    const breakdown = breakdownData;

    return {
      onsite,
      hybrid,
      remote,
      total,
      headline: headlineKey,
      headlineDefault,
      breakdown: breakdownData,
      breakdownData,
      onsitePercent,
      hybridPercent,
      remotePercent,
    };
  }, [remotenessDistribution]);

  // Calculate language distribution (English vs Local)
  const languageData = React.useMemo(() => {
    const english = languageRequirements['English'] || 0;
    const finnish = languageRequirements['Finnish'] || languageRequirements['Suomi'] || 0;
    const swedish = languageRequirements['Swedish'] || languageRequirements['Svenska'] || 0;
    const local = finnish + swedish;
    return { english, local, total: english + local };
  }, [languageRequirements]);

  // Format salary value
  const salaryValue = React.useMemo(() => {
    if (!salaryStats) return null;
    const avg = salaryStats.average?.min || salaryStats.median?.min || 0;
    if (avg === 0) return null;
    return `€${Math.round(avg / 1000)}k/mo`;
  }, [salaryStats]);

  // Get background image (use prop if provided, otherwise auto-detect from location)
  const effectiveBackgroundImage = React.useMemo(() => {
    // If a backgroundImage prop is provided as a string (for backward compatibility), use it
    if (backgroundImage && typeof backgroundImage === 'string') {
      return { src: backgroundImage, srcset: '' };
    }

    // Otherwise, auto-detect from location using the generated S3 images
    const knownSet = new Set([...STATIC_LOCATIONS_WITH_BACKGROUNDS, ...(knownLocationBackgrounds ?? [])]);
    const resolved = resolveBackgroundImageLocation(location, knownSet);
    if (!resolved) return undefined;
    return {
      src: getLocationBackgroundImageDefaultSrc(resolved),
      srcset: getLocationBackgroundImageSrcset(resolved),
    };
  }, [backgroundImage, location, knownLocationBackgrounds]);

  // Handle removing location filter
  const handleRemoveLocation = React.useCallback(() => {
    const { location: _, ...remainingFilters } = parsedFilters;
    const nextState: IndexUiState = {
      refinementList: remainingFilters,
      page: 0,
    };
    if (typeof query === 'string' && query.trim().length > 0) {
      nextState.query = query;
    }
    pushIndexUiState(nextState);
  }, [parsedFilters, pushIndexUiState, query]);

  // Build filter state for pill buttons
  const buildFilterState = React.useCallback(
    (attribute: string, value: string): IndexUiState => {
      const baseRefinements = Object.entries(parsedFilters).reduce<Record<string, string[]>>(
        (acc, [key, val]) => {
          if (Array.isArray(val) && val.length > 0) {
            acc[key] = [...val];
          }
          return acc;
        },
        {}
      );

      const trimmedValue = value.trim();
      if (trimmedValue.length > 0) {
        const existing = baseRefinements[attribute] ? [...baseRefinements[attribute]] : [];
        if (!existing.includes(trimmedValue)) {
          existing.push(trimmedValue);
        }
        baseRefinements[attribute] = existing;
      }

      const nextState: IndexUiState = {
        refinementList: baseRefinements,
        page: 0,
      };

      if (typeof query === 'string' && query.trim().length > 0) {
        nextState.query = query;
      }

      return nextState;
    },
    [parsedFilters, query]
  );

  const hasEnglishFilter = parsedFilters.requiredLanguages?.includes('English');
  const hasRemoteFilter = parsedFilters.remoteness?.some((r) =>
    r.toLowerCase().includes('remote')
  );
  const hasHighSalaryFilter = parsedFilters.salaryRangeMin && parsedFilters.salaryRangeMin.length > 0;

  // Calculate data for SEO
  const remotePercentage = workModelData.total > 0
    ? Math.round(((workModelData.remote + workModelData.hybrid) / workModelData.total) * 100)
    : 0;
  const avgSalaryNum = salaryStats?.average?.min || salaryStats?.median?.min || 0;
  const avgSalaryFormatted = avgSalaryNum > 0 ? `€${Math.round(avgSalaryNum / 1000)}k` : 'N/A';
  const companyDistributionEntries = locationTrendsData?.trends?.companyDistribution
    ? Object.entries(locationTrendsData.trends.companyDistribution)
    : [];
  const topCompanies = companyDistributionEntries
    .sort(([, a], [, b]) => (b as number) - (a as number))
    .slice(0, 3)
    .map(([name]) => name);
  const topCompany = topCompanies[0] ?? null;

  // Extract top industries from fieldDistribution
  const fieldDistributionEntries = locationTrendsData?.trends?.fieldDistribution
    ? Object.entries(locationTrendsData.trends.fieldDistribution)
    : [];
  const topIndustries = fieldDistributionEntries
    .sort(([, a], [, b]) => (b as number) - (a as number))
    .slice(0, 3)
    .map(([name]) => name);
  // Only use lastUpdated if it comes from stable data (salaryStats)
  // Never use Date.now() or new Date() here to avoid hydration mismatches
  const lastUpdated = salaryStats?.lastUpdated || null;
  const lastUpdatedDate = lastUpdated ? new Date(lastUpdated) : null;
  const locale = intl.locale as 'en' | 'fi' | 'sv';

  // Full set of currently-applied search filters (location + everything else), used to build a
  // results heading that reflects all of them — not just location. Guarantees a 'location' entry
  // even if the client-side parsedFilters doesn't carry one the way server-side initialFilters does.
  const activeFilters = React.useMemo(() => {
    const built = buildActiveFiltersFromInitialFilters(parsedFilters);
    if (location && !built.some((f) => f.type === 'location')) {
      built.unshift({ type: 'location', value: location, rawValue: location });
    }
    return built;
  }, [parsedFilters, location]);

  const resultsHeadingText = React.useMemo(
    () => generateResultsHeading(activeFilters, nbHits, locale),
    [activeFilters, nbHits, locale]
  );

  // Translate location name based on locale
  const translatedLocation = React.useMemo(() => {
    if (!location) return location;

    // Parse location (format: "City, Country" or "City_Country")
    const parts = location.includes(',')
      ? location.split(',').map(p => p.trim())
      : location.split('_').map(p => p.trim());

    if (parts.length < 2) {
      // Just city name, try to translate it
      const cityName = parts[0].trim();
      for (const [englishCity, variants] of Object.entries(CITY_NAME_MAPPINGS)) {
        if (
          cityName.toLowerCase() === variants.en.toLowerCase() ||
          cityName.toLowerCase() === variants.fi.toLowerCase() ||
          cityName.toLowerCase() === variants.sv.toLowerCase()
        ) {
          return variants[locale];
        }
      }
      return cityName;
    }

    const cityPart = parts[0].trim();
    const countryPart = parts[1].trim();

    // Translate city name
    let translatedCity = cityPart;
    for (const [englishCity, variants] of Object.entries(CITY_NAME_MAPPINGS)) {
      if (
        cityPart.toLowerCase() === variants.en.toLowerCase() ||
        cityPart.toLowerCase() === variants.fi.toLowerCase() ||
        cityPart.toLowerCase() === variants.sv.toLowerCase()
      ) {
        translatedCity = variants[locale];
        break;
      }
    }

    // Translate country name
    let translatedCountry = countryPart;
    for (const [countryKey, variants] of Object.entries(COUNTRY_NAME_MAPPINGS)) {
      if (
        countryPart.toLowerCase() === variants.en.toLowerCase() ||
        countryPart.toLowerCase() === variants.fi.toLowerCase() ||
        countryPart.toLowerCase() === variants.sv.toLowerCase()
      ) {
        translatedCountry = variants[locale];
        break;
      }
    }

    return `${translatedCity}, ${translatedCountry}`;
  }, [location, locale]);

  // fi H1 drops the country ("Avoimet työpaikat Helsinki") to match the
  // flat "Avoimet työpaikat [City]" convention Finnish job boards use,
  // rather than a literal "in {location}" translation.
  const marketHeaderTitleLocation = React.useMemo(() => {
    if (locale !== 'fi' || !translatedLocation) return translatedLocation;
    return translatedLocation.split(',')[0].trim();
  }, [translatedLocation, locale]);

  const backgroundImageAlt = React.useMemo(
    () => formatLocationBackgroundImageAltFromIntl(formatMessage, translatedLocation),
    [formatMessage, translatedLocation]
  );

  // Extract Finland averages from locationTrendsData (if available from API)
  const finlandAverages = React.useMemo(() => {
    // First try to get from locationTrendsData (from API)
    const finlandDataFromTrends = locationTrendsData?.finlandAverages;
    if (finlandDataFromTrends) {
      return {
        avgSalaryNum: finlandDataFromTrends.avgSalaryNum,
        avgSalaryFormatted: finlandDataFromTrends.avgSalaryFormatted,
        remotePercentage: finlandDataFromTrends.remotePercentage,
        englishLanguagePercentage: finlandDataFromTrends.englishLanguagePercentage,
      };
    }

    // Fallback to pageContext data
    const finlandData = (pageContext as any)?.data?.finlandAverages;
    if (!finlandData) return undefined;

    return {
      avgSalaryNum: finlandData.avgSalaryNum,
      avgSalaryFormatted: finlandData.avgSalaryFormatted,
      remotePercentage: finlandData.remotePercentage,
      englishLanguagePercentage: finlandData.englishLanguagePercentage,
    };
  }, [pageContext, locationTrendsData]);

  // Generate rich textual blurbs for crawlers and users
  const locationHeaderText = React.useMemo(() => {
    return generateLocationMarketHeaderText({
      location,
      locale,
      nbHits,
      sampleCount,
      remotePercentage,
      avgSalaryNum,
      avgSalaryFormatted,
      languageTotals: {
        english: languageData.english,
        local: languageData.local,
        total: languageData.total,
      },
      topCompanies,
      topIndustries,
      finlandAverages,
    });
  }, [
    location,
    locale,
    nbHits,
    sampleCount,
    remotePercentage,
    avgSalaryNum,
    avgSalaryFormatted,
    languageData.english,
    languageData.local,
    languageData.total,
    topCompanies,
    topIndustries,
    finlandAverages,
  ]);

  // Generate Q&A structure for AI Overviews. These are just the answer fragments — the
  // question text and (for remote) the "Yes, " prefix are rendered separately by their own
  // localized Msg elements in MarketHeaderSeoBlocks.tsx, so these must NOT repeat the question.
  const qaContent = React.useMemo(() => {
    const remote = remotePercentage > 0
      ? formatMessage(
          { id: 'marketHeader.qa.remoteAnswerWithPercent', defaultMessage: '{percent}% of roles are remote or hybrid.' },
          { percent: remotePercentage }
        )
      : formatMessage({ id: 'marketHeader.qa.remoteAnswerFallback', defaultMessage: 'Remote work options are available.' });
    const salary = avgSalaryNum > 0
      ? formatMessage(
          { id: 'marketHeader.qa.salaryAnswerWithValue', defaultMessage: 'Average salary is {salary}.' },
          { salary: `${avgSalaryFormatted}/month` }
        )
      : formatMessage({ id: 'marketHeader.qa.salaryAnswerFallback', defaultMessage: 'Salary data available.' });
    const language = languageData.total > 0
      ? formatMessage(
          { id: 'marketHeader.qa.languageAnswer', defaultMessage: '{percent}% of jobs require English.' },
          { percent: Math.round((languageData.english / languageData.total) * 100) }
        )
      : formatMessage({ id: 'marketHeader.qa.languageAnswerFallback', defaultMessage: 'Language requirements vary by role.' });
    return { remote, salary, language };
  }, [formatMessage, remotePercentage, avgSalaryFormatted, avgSalaryNum, languageData]);

  // Generate nearby locations (major Finnish cities)
  const nearbyLocations = React.useMemo(() => {
    const majorCities = [
      { name: 'Helsinki', slug: 'Helsinki_Finland', displayName: locale === 'sv' ? 'Helsingfors' : 'Helsinki' },
      { name: 'Tampere', slug: 'Tampere_Finland', displayName: locale === 'sv' ? 'Tammerfors' : 'Tampere' },
      { name: 'Turku', slug: 'Turku_Finland', displayName: locale === 'sv' ? 'Åbo' : 'Turku' },
      { name: 'Oulu', slug: 'Oulu_Finland', displayName: locale === 'sv' ? 'Uleåborg' : 'Oulu' },
      { name: 'Espoo', slug: 'Espoo_Finland', displayName: locale === 'sv' ? 'Esbo' : 'Espoo' },
      { name: 'Vantaa', slug: 'Vantaa_Finland', displayName: locale === 'sv' ? 'Vanda' : 'Vantaa' },
    ];
    // Filter out current location (check both name and display name)
    // Use translatedLocation to get the localized city name for comparison
    const currentCity = translatedLocation ? translatedLocation.split(',')[0].trim() : location.split(',')[0].trim();
    return majorCities
      .filter(city => city.name !== currentCity && city.displayName !== currentCity)
      .slice(0, 6);
  }, [location, translatedLocation, locale]);

  // Generate FAQ questions for structured data
  const faqQuestions = React.useMemo(() => {
    const questions = [];
    if (avgSalaryNum > 0) {
      questions.push({
        question: `What is the average salary in ${translatedLocation}?`,
        answer: `The average salary in ${translatedLocation} is ${avgSalaryFormatted}/month based on ${salaryStats?.sampleSize || 0} job postings.`,
      });
    }
    if (remotePercentage > 0) {
      questions.push({
        question: `Is ${translatedLocation} remote-friendly?`,
        answer: `Yes, ${remotePercentage}% of job postings in ${translatedLocation} offer remote or hybrid work arrangements.`,
      });
    }
    if (languageData.total > 0) {
      const englishPercent = Math.round((languageData.english / languageData.total) * 100);
      questions.push({
        question: `What languages are required for jobs in ${translatedLocation}?`,
        answer: `${englishPercent}% of job postings in ${translatedLocation} require English, with ${100 - englishPercent}% requiring Finnish, Swedish, or other languages.`,
      });
    }
    return questions;
  }, [translatedLocation, avgSalaryNum, avgSalaryFormatted, salaryStats, remotePercentage, languageData]);

  // Generate structured data (JSON-LD)
  const structuredData = React.useMemo(() => {
    const locationSegment = locale === 'fi' ? 'alue' : locale === 'sv' ? 'omrade' : 'location';
    const jobsSegment = JOBS_LISTING_SEGMENT[locale as keyof typeof JOBS_LISTING_SEGMENT];
    const locationSlug = buildEncodedLocationSrpPathSegment(location, locale);
    const datasetUrl = `${BASE_URL}/${locale}/${jobsSegment}/${locationSegment}/${locationSlug}`;

    // Generate significant links to nearby locations
    const significantLinks = nearbyLocations.map(city => {
      const cityLocationSegment = locale === 'fi' ? 'alue' : locale === 'sv' ? 'omrade' : 'location';
      const cityJobsSegment = JOBS_LISTING_SEGMENT[locale as keyof typeof JOBS_LISTING_SEGMENT];
      const encodedSlug = buildEncodedLocationSrpPathSegment(city.slug, locale);
      return `${BASE_URL}/${locale}/${cityJobsSegment}/${cityLocationSegment}/${encodedSlug}`;
    });

    const dataset = {
      '@context': 'https://schema.org',
      '@type': 'Dataset',
      name: `2026 Job Market Analytics for ${translatedLocation}`,
      description: `Live data on salary trends, language requirements, and work-model distribution for ${translatedLocation}.`,
      url: datasetUrl,
      license: 'https://creativecommons.org/licenses/by/4.0/',
      creator: {
        '@type': 'Organization',
        name: 'JobCrawls',
        url: BASE_URL,
      },
      ...(lastUpdatedDate ? {
        datePublished: lastUpdatedDate.toISOString(),
        dateModified: lastUpdatedDate.toISOString(),
      } : {}),
      spatialCoverage: {
        '@type': 'Place',
        name: translatedLocation,
        address: {
          '@type': 'PostalAddress',
          addressLocality: translatedLocation.split(',')[0].trim(),
          addressCountry: location.includes('Finland') || location.includes('Suomi') || translatedLocation.includes('Finland') ? 'FI' : undefined,
        },
      },
      variableMeasured: [
        {
          '@type': 'PropertyValue',
          name: 'Average Salary',
          value: avgSalaryFormatted,
        },
        {
          '@type': 'PropertyValue',
          name: 'Remote Percentage',
          value: `${remotePercentage}%`,
        },
        ...(topCompany ? [{
          '@type': 'PropertyValue',
          name: 'Top Hiring Company',
          value: topCompany,
        }] : []),
        ...(languageData.total > 0 ? [{
          '@type': 'PropertyValue',
          name: 'English Language Requirement Percentage',
          value: `${Math.round((languageData.english / languageData.total) * 100)}%`,
        }] : []),
      ],
      ...(salaryStats?.sampleSize ? {
        numberOfItems: salaryStats.sampleSize,
      } : {}),
      ...(significantLinks.length > 0 ? {
        significantLink: significantLinks,
      } : {}),
    };

    const faqPage = faqQuestions.length > 0 ? {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqQuestions.map(q => ({
        '@type': 'Question',
        name: q.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: q.answer,
        },
      })),
    } : null;

    return { dataset, faqPage };
  }, [location, translatedLocation, locale, lastUpdatedDate, avgSalaryFormatted, remotePercentage, topCompany, salaryStats, faqQuestions, nearbyLocations, languageData]);

  return {
    intl,
    formatMessage,
    locale,
    parsedFilters,
    salaryStats,
    sampleCount,
    salaryRange,
    salaryPercentileSeoData,
    workModelData,
    languageData,
    salaryValue,
    effectiveBackgroundImage,
    handleRemoveLocation,
    buildFilterState,
    hasEnglishFilter,
    hasRemoteFilter,
    hasHighSalaryFilter,
    remotePercentage,
    avgSalaryNum,
    avgSalaryFormatted,
    topCompany,
    topCompanies,
    topIndustries,
    lastUpdatedDate,
    translatedLocation,
    marketHeaderTitleLocation,
    resultsHeadingText,
    backgroundImageAlt,
    finlandAverages,
    locationHeaderText,
    qaContent,
    nearbyLocations,
    faqQuestions,
    structuredData,
    description,
  };
}

export type MarketHeaderData = ReturnType<typeof useMarketHeaderData>;
